import Backbone from 'backbone';

import pubSub from './events/pubsub.js';
import ProjectUtil from './utils/project-util.js';
import LoaderView from './views/loader.js';
import NavView from './views/nav.js';
import PageView from './views/page.js';

// router.js
class Router extends Backbone.Router {
	constructor(options) {
		super(options);
		this.firstPageRequest = true;
		this.activePage = null;
		this.activePageRoute = null;
		this.requestedPageID = null;
		this.requestedContentAr = [];
	}

	/**
	 * Declare routes
	 * @returns {Object} - routes
	 */
	routes() {
		return {
			'*path': 'defaultRoute'
		};
	}

	initialize() {
		this.pubSub = pubSub; // define here, because initialize is called in the constructor's super, and pubSub is not defined yet
		// listen to pageChangeRequest events
		this.listenTo(this.pubSub, "pageChangeRequest", this.handlePageChangeRequest);
		// listen to view hidden events
		this.listenTo(this.pubSub, "viewHideComplete", this.handleViewHidden);
		// listen to modal close events
		this.listenTo(this.pubSub, "modalClosed", this.handleModalClosed);
		// listen to video modal close events
		this.listenTo(this.pubSub, 'videoModalClosed', this.handleVideoModalClosed);
		// listen to hide page loader events
		this.listenTo(this.pubSub, "hidePageLoader", this.hidePageLoader);
	}

	ready() {
		// add cmsJSON and pubSub to ProjectUtil
		ProjectUtil.cmsJSON = this.cmsJSON;
		ProjectUtil.pubSub = this.pubSub;
		ProjectUtil.initialPage = true;

		// initialize lazyload
		// Just add loading="lazy" to images and iframes to enable lazy loading - consider adding lazyloader to the project later

		// build the nav.
		this.buildSimpleView('nav', NavView);
		/*
		// build the background manager
		this.buildSimpleView('bgManager', BackgroundManagerView);
		// build the footer
		this.buildSimpleView('footer', FooterView);
		// build videoModal
		this.buildSimpleView('videoModal', VideoModalView);
		// build modalManager
		this.buildModalManager();
		// build messageModal
		this.buildSimpleView('messageModal', MessageModalView);
		*/
		// build loader
		//this.loader = this.buildSimpleView('loader', LoaderView);
		this.loader = this.buildSimpleView('loader', LoaderView, { pageLoader: true }, document.getElementById('loader'));
		//window.loader = this.loader;

		// store pageload data - determine if pageload data is type page or type post
		if (this.cmsJSON) {
			/*// TEMPORARY FUNCTIONALITY --- ADD ALL PAGES FROM LOCAL JSON FOR TESTING
			if (this.cmsJSON.routes) {
				this.cmsJSON.routes.forEach(tRoute => {
					ProjectUtil.pageData[tRoute.route] = { type: tRoute.join_type, data: tRoute };
				});
			}*/
			// END TEMPORARY FUNCTIONALITY

			// add default page to ProjectUtil.pageData to prevent double loading
			if (this.cmsJSON.page) {
				ProjectUtil.pageData[ProjectUtil.cmsJSON.page.route] = { type: this.getPageType(ProjectUtil.cmsJSON), data: ProjectUtil.cmsJSON.page };
			}
			// if there is a modal, add it to ProjectUtil.pageData to prevent double loading
			if (this.cmsJSON.modal) {
				ProjectUtil.pageData[ProjectUtil.cmsJSON.modal.route] = { type: 'modal', data: ProjectUtil.cmsJSON.modal };
			}

			// After main page is interactive
			// wait a couple of seconds to load the page template
			setTimeout(() => {
				if ('requestIdleCallback' in window) {
					window.requestIdleCallback(() => {
						import(/* webpackChunkName: "page" */ '../handlebars/client/page.hbs');
					});
				} else {
					// Fallback for unsupported browsers - give a couple extra seconds before loading
					setTimeout(() => {
						import(/* webpackChunkName: "page" */ '../handlebars/client/page.hbs');
					}, 2000);
				}
			}, 2000);
		}
	}

	handlePageChangeRequest(pageID) {
		this.navigate(pageID, {
			trigger: true
		});
	}

	hidePage(routeFunction, pageID, contentAr) {
		if (this.firstPageRequest) {
			this.firstPageRequest = false;
			this.requestedPageID = pageID;
			this.requestedContentAr = contentAr;
			return false; // false means no page to hide
		}
		if (this.activePage) {
			this.requestedRouteFunction = routeFunction;
			this.requestedPageID = pageID;
			this.requestedContentAr = contentAr;
			this.activePage.hide();
			return true; // true means there's a page to hide
		} else {
			return false; // false means no page to hide
		}
	}

	handleViewHidden(tView) {
		if (tView === this.activePage) {
			this.destroyActivePage();
			// set page scroll to the top
			window.scrollTo(0, 0);
			if (this.requestedRouteFunction &&
				this[this.requestedRouteFunction] &&
				typeof this[this.requestedRouteFunction] === 'function') {
				this[this.requestedRouteFunction].apply(this, this.requestedContentAr);
				return;
			}
			/*if (this.requestedContentAr && this.requestedContentAr.length) {
				console.log('[Router].handleViewHidden: this.requestedContentAr: ', this.requestedContentAr);
			}*/
			this.page.apply(this, this.requestedContentAr);
		}
	}

	getPageType(dataObj) {
		return (dataObj.hasOwnProperty('page') && dataObj.page.join_type === 'post') ? 'post' : 'page';
	}

	getRouteData(route, routeName, cb_success, cb_fail) {
		if (ProjectUtil.pageData[routeName]) {
			if (ProjectUtil.pageData[routeName].type === 'modal') {
				cb_success.call(null, { ...ProjectUtil.pageData[routeName], pageData: ProjectUtil.pageData[ProjectUtil.cmsJSON.page.route] });
				return;
			}
			if (ProjectUtil.pageData[routeName].data.dynamic_data !== 1) {
				cb_success.call(null, ProjectUtil.pageData[routeName]);
				return;
			}
		}

		if (this.firstPageRequest) {
			cb_success.call(null, { type: 'page', data: ProjectUtil.cmsJSON.page });
			return;
		}

		this.loader.showLoader(() => {
			ProjectUtil.requestAPI(`route=${route}&site_json=0`, {},
				(response) => {
					//this.loader.closeLoader();
					if (response.modal) {
						this.savePageData(response.modal.route, 'modal', response.modal);
						if (!ProjectUtil.pageData.hasOwnProperty(response.page.route)) {
							this.savePageData(response.page.route, 'page', response.page);
						}
						cb_success.call(null, { type: 'modal', data: response.modal, pageData: { type: 'page', data: response.page } });
					} else if (response.page) {
						const tType = this.getPageType(response);
						this.savePageData(routeName, tType, response.page);
						cb_success.call(null, { type: tType, data: response.page });
					} else {
						console.error('no data in response: ', response);
						this.navigate('404', {
							trigger: true
						});
					}
				},
				() => {
					this.loader.closeLoader();
					if (cb_fail) {
						cb_fail.call(null);
					}
				}
			);
		});
	}

	savePageData(tRoute, tType, tData) {
		if (tRoute.indexOf('pnum-') > -1) {
			return;
		}
		ProjectUtil.pageData[tRoute] = { type: tType, data: tData };
	}

	handleModalClosed() {
		this.navigate(this.activePageRoute);
	}

	handleVideoModalClosed() {
		this.handleModalClosed();
	}

	hidePageLoader() {
		if (this.loader) {
			this.loader.closeLoader();
		}
	}

	/**
	 * Update document title to match the title of the current page from JSON
	 */
	updatePageTitle(dataObj) {
		if (dataObj && dataObj.hasOwnProperty('title') && dataObj.title.length) {
			document.title = dataObj.title + ' - The Tasting Alliance';
		}
	}

	createModel(data, extraData) {
		return new Backbone.Model(extraData ? { ...data, ...extraData } : data);
	}

	getView(viewsID, ViewClass, model, el) {
		let tOptions = { model, pubSub };
		if (el) {
			tOptions.el = el;
		}
		return new ViewClass(tOptions);
	}

	buildSimpleView(tID, tClass, tOptions, tEl) {
		const view = this.getView(tID, tClass, this.createModel(this.cmsJSON, tOptions), tEl);
		return view.render({});
	}

	destroyActivePage() {
		if (this.activePage) {
			this.activePage.destroy();
			this.activePage = null;
			this.activePageRoute = null;
		}
	}

	/*buildModalManager() {
		const renderObj = this.cmsJSON;
		const model = this.createModel(renderObj);
		const tView = this.getView('modalManager', ModalManagerView, model);  // ModalManagerView does not exist, needs to be created if we intend to add modals
		tView.render();
	}*/

	page(routeString, dataObj) {
		//console.log('-------------------------------');
		//console.log('[Router].page:', routeString, dataObj);
		//console.log('-------------------------------');

		if (!this.hidePage('page', routeString, [routeString, dataObj])) {
			// update document title
			this.updatePageTitle(dataObj);

			const tInitialPage = ProjectUtil.initialPage;
			const renderObj = { site: this.cmsJSON.site };
			const model = this.createModel(renderObj, { page: dataObj });
			const tView = this.getView(routeString, PageView, model);


			tView.render();

			this.activePage = tView;
			this.activePageRoute = routeString;

			this.pubSub.trigger("pageChange", { page: routeString, initialPage: tInitialPage });

			/*if (!tInitialPage) {
				window.trackPageView(routeString);
			}*/
		}
	}

	modal(/*routeString, dataObj*/) {
		/*const renderObj = { site: this.cmsJSON.site };
		const model = this.createModel(renderObj, { modal: dataObj });
	
		this.pubSub.trigger('requestPageModal', { name: routeString, model });
	
		this.activeModal = routeString;
		if (!ProjectUtil.initialPage) {
			window.trackPageView(routeString);
		}*/
	}

	defaultRoute() {
		const tRoute = Backbone.history.getFragment().split('?')[0];
		const tRouteString = (tRoute === '') ? 'home' : tRoute;

		// handle querystring changes - filtering updates
		if (this.activePageRoute) {
			if (this.activePageRoute === tRoute) {
				this.pubSub.trigger('updateQsFilters', true); // true to trigger content replace
				return;
			}
		}

		this.getRouteData(tRoute, tRouteString, (dataObj) => {
			if (dataObj) {
				if (dataObj.type === 'page') {
					this.page(tRouteString, dataObj.data);
				} else {
					this.modal(tRouteString, dataObj.data);
					if (!this.activePage && dataObj.hasOwnProperty('pageData')) {
						this.page(dataObj.pageData.data.route, dataObj.pageData.data);
					}
				}
			}
		}, () => {
			this.pubSub.trigger('requestAlertModal', {
				title: "Content Load Failed.",
				message: "We are unable to load that content at this time. Please try again later."
			});
		});
	}
}



const AppRouter = {
	initialize: (obj) => {
		const app = new Router();
		//window.App = app; // Use this for debugging only
		app.cmsJSON = obj.cms;
		app.ready();


		// blur anything that has been clicked
		document.body.addEventListener('click', (e) => {
			if (e.target.tagName === 'A') {
				e.target.blur();
			}
		});

		// Any click that occurs on the page that is not an external link is handled here.
		document.body.addEventListener('click', (e) => {
			const target = e.target.closest('a:not([href^="http://"], [href^="https://"], [href^="tel:"], [href^="mailto:"], .out)');
			if (target) {
				const href = target.getAttribute('href');
				const url = href.replace(/^\//, '').replace('#!\/', ''); // The first replace is for '/' at the beginning, the second is for '#!/' anywhere

				if (!e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
					// allow shift+click to open in new tab
					e.preventDefault();

					// ignore clicks on disabled buttons, or buttons that link to the current page (Backbone.history.fragment), or buttons that don't do anything (href="#")
					if (target.classList.contains('disabled') || target.classList.contains('ignore-click') || url === Backbone.history.fragment || href === "#") {
						return false;
					}

					app.navigate(url, {
						trigger: true
					});

					return false;
				}
			}
		});

		// initiate history
		Backbone.history.start({
			pushState: true,
			root: window.GlobalsObj.ROOT_PATH.split(' ').join('%20')
		});
	}/*,

	dataError: () => {
		// create message modal
		new MessageModalView({}).render();
		// trigger message modal
		pubSub.trigger('requestAlertModal', { title: "Data Error", message: "There was an error loading site data. Please try again later.", close: "okay" });
	}*/
};

export default AppRouter;